import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from '../../context/ToastContext';

const TOAST_ICONS = {
  success: 'check-circle',
  error: 'circle-xmark',
  warning: 'triangle-exclamation',
  info: 'circle-info',
};

const TOAST_STYLES = {
  success: {
    bg: 'bg-emerald-50 dark:bg-emerald-500/10',
    border: 'border-emerald-200 dark:border-emerald-500/30',
    icon: 'text-emerald-500',
    text: 'text-emerald-800 dark:text-emerald-200',
    progress: 'bg-emerald-500',
  },
  error: {
    bg: 'bg-rose-50 dark:bg-rose-500/10',
    border: 'border-rose-200 dark:border-rose-500/30',
    icon: 'text-rose-500',
    text: 'text-rose-800 dark:text-rose-200',
    progress: 'bg-rose-500',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-500/10',
    border: 'border-amber-200 dark:border-amber-500/30',
    icon: 'text-amber-500',
    text: 'text-amber-800 dark:text-amber-200',
    progress: 'bg-amber-500',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-500/10',
    border: 'border-blue-200 dark:border-blue-500/30',
    icon: 'text-blue-500',
    text: 'text-blue-800 dark:text-blue-200',
    progress: 'bg-blue-500',
  },
};

const Toast = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  const styles = TOAST_STYLES[toast.type] || TOAST_STYLES.info;
  const icon = toast.icon || TOAST_ICONS[toast.type] || TOAST_ICONS.info;

  useEffect(() => {
    if (toast.duration <= 0) return;

    const startTime = Date.now();
    const endTime = startTime + toast.duration;

    const updateProgress = () => {
      const now = Date.now();
      const remaining = Math.max(0, endTime - now);
      const percent = (remaining / toast.duration) * 100;
      setProgress(percent);

      if (percent > 0) {
        requestAnimationFrame(updateProgress);
      }
    };

    requestAnimationFrame(updateProgress);

    // Déclencher l'animation de sortie avant la suppression
    const exitTimeout = setTimeout(() => {
      setIsExiting(true);
    }, toast.duration - 300);

    return () => {
      clearTimeout(exitTimeout);
    };
  }, [toast.duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onRemove(toast.id), 300);
  };

  return (
    <div
      className={`
        relative overflow-hidden rounded-xl border shadow-lg backdrop-blur-sm
        transition-all duration-300 ease-out
        ${styles.bg} ${styles.border}
        ${isExiting 
          ? 'opacity-0 translate-x-4 scale-95' 
          : 'opacity-100 translate-x-0 scale-100'
        }
        animate-in slide-in-from-right-4 fade-in duration-300
      `}
      style={{ minWidth: '320px', maxWidth: '420px' }}
    >
      <div className="flex items-start gap-3 p-4">
        {/* Icon */}
        <div className={`flex-shrink-0 mt-0.5 ${styles.icon}`}>
          <FontAwesomeIcon icon={icon} className="text-lg" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {toast.title && (
            <p className={`font-semibold text-sm mb-0.5 ${styles.text}`}>
              {toast.title}
            </p>
          )}
          <p className={`text-sm ${styles.text} ${toast.title ? 'opacity-80' : ''}`}>
            {toast.message}
          </p>
          
          {/* Action Button */}
          {toast.action && (
            <button
              onClick={() => {
                toast.action.onClick();
                handleClose();
              }}
              className={`mt-2 text-xs font-semibold underline underline-offset-2 hover:no-underline ${styles.text}`}
            >
              {toast.action.label}
            </button>
          )}
        </div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className={`flex-shrink-0 p-1 rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/10 ${styles.text} opacity-60 hover:opacity-100`}
        >
          <FontAwesomeIcon icon="xmark" className="text-sm" />
        </button>
      </div>

      {/* Progress Bar */}
      {toast.duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/5 dark:bg-white/5">
          <div
            className={`h-full transition-none ${styles.progress}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

const ToastContainer = () => {
  const { toasts, removeToast } = useToast();

  // Render dans un portal pour être au-dessus de tout
  return createPortal(
    <div
      className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-none"
      style={{ maxHeight: 'calc(100vh - 32px)' }}
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <Toast toast={toast} onRemove={removeToast} />
        </div>
      ))}
    </div>,
    document.body
  );
};

export default ToastContainer;
